import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Save, 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Edit3,
  Wrench,
  Sparkles,
  Loader2,
  User
} from 'lucide-react';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { authInterceptor } from '../lib/auth-interceptor';
import { formatCurrency } from '../lib/utils';
import { formatPhone } from '../lib/validation';
import { useServices } from '../hooks/use-services';
import { useEstimates } from '../hooks/use-estimates';
import { AddServiceDialog } from '../components/estimates/add-service-dialog';
import { formatEstimateUnitLabel, mergeEstimateUnitOptions } from '../lib/estimate-units';
import { SERVICE_TYPES } from '../lib/service-types';
import { buildAiUserDescription, parseLineItemQuantity } from '../lib/ai-description-input';

function formatServiceLabel(serviceType?: string): string {
  const raw = serviceType ?? '';
  if (!raw || raw === 'Custom') return 'Custom';
  const lower = raw.toLowerCase();
  const known: Record<string, string> = {
    roofing: 'Roofing', roof: 'Roofing', fence: 'Fence', deck: 'Deck',
    windows: 'Windows', gutters: 'Gutters', siding: 'Siding', flooring: 'Flooring',
    painting: 'Painting', electrical: 'Electrical', plumbing: 'Plumbing', hvac: 'HVAC',
  };
  return known[lower] ?? (lower.charAt(0).toUpperCase() + lower.slice(1));
}

export default function EstimateEditPage() {
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id: string }>();
  const estimateId = id ? parseInt(id) : null;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [estimate, setEstimate] = useState<any>(null);
  const [editedEstimate, setEditedEstimate] = useState<any>(null);
  const [generatingDescription, setGeneratingDescription] = useState<number | null>(null);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const addServiceLineRef = useRef<number | null>(null);

  // Fetch estimate data
  const { data: estimateData, error, isLoading: estimateLoading } = useQuery({
    queryKey: [`/api/protected/estimates`, estimateId],
    queryFn: async () => {
      const response = await fetch(`/api/protected/estimates?id=${estimateId}`, {
        headers: {
          ...authInterceptor.addAuthHeaders()
        }
      });
      if (!response.ok) throw new Error('Failed to fetch estimate');
      return response.json();
    },
    enabled: estimateId !== null && !isNaN(estimateId),
  });

  // Fetch clients for client name
  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['/api/protected/clients'],
    enabled: estimateId !== null && !isNaN(estimateId),
  });

  // Fetch available services via shared hook (ensures correct shape: value, label, unit, laborRate)
  const { services, isLoading: servicesLoading } = useServices();
  const { updateEstimateMutation: updateEstimateAgentMutation } = useEstimates();

  // Fetch agents for Assigned Agent (client portal coordinator)
  const { data: agentsList = [] } = useQuery({
    queryKey: ['/api/protected/agents'],
    queryFn: async () => {
      const response = await fetch('/api/protected/agents', {
        credentials: 'include',
        headers: { ...authInterceptor.addAuthHeaders() },
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : (data.agents || []);
    },
    enabled: !!estimateId && !!estimateData,
  });

  useEffect(() => {
    if (estimateData && typeof estimateData === 'object') {
      console.log('🔍 DEBUG: Estimate data received:', estimateData);
      console.log('🔍 DEBUG: Clients data received:', clients);
      
      // Try multiple ways to find the client
      let client = null;
      const estimateClientId = (estimateData as any).clientId || (estimateData as any).client_id;
      
      if (Array.isArray(clients) && clients.length > 0) {
        client = clients.find((c: any) => {
          console.log('🔍 DEBUG: Checking client:', c, 'against estimateClientId:', estimateClientId);
          return c.id === estimateClientId;
        });
        
        if (!client) {
          // Try to find by other possible fields
          client = clients.find((c: any) => 
            c.id === (estimateData as any).client_id || 
            c.id === (estimateData as any).clientId ||
            c.client_id === (estimateData as any).id ||
            c.estimate_id === (estimateData as any).id
          );
        }
      }
      
      console.log('🔍 DEBUG: Found client:', client);
      
      const estimateWithClient = {
        ...(estimateData as any),
        client: client, // Include the full client object
        clientName: client ? `${client.first_name || client.firstName || ''} ${client.last_name || client.lastName || ''}`.trim() : 
          `Client ID: ${(estimateData as any).clientId || (estimateData as any).client_id || 'Not found'}`
      };
      
      console.log('🔍 DEBUG: Final estimate with client:', estimateWithClient);
      
      setEstimate(estimateWithClient);
      setEditedEstimate(estimateWithClient);
      setIsLoading(false);

      // Signed or converted estimates cannot be edited — redirect to list
      const status = (estimateWithClient as any).status;
      if (status === 'accepted' || status === 'converted') {
        toast({
          title: 'Cannot edit',
          description: 'Signed estimates are locked. Prices and descriptions cannot be changed.',
          variant: 'destructive',
        });
        setLocation('/estimates');
      }
    } else if (error) {
      console.error('❌ EstimateEditPage: Error loading estimate:', error);
      setIsLoading(false);
    } else if (estimateLoading) {
      // Still loading
    } else {
      setIsLoading(false);
    }
  }, [estimateData, estimateLoading, error, estimateId]); // REMOVED clients and clientsLoading to prevent infinite loop

  const updateEstimateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/protected/estimates?id=${estimateId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authInterceptor.addAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update estimate');
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Estimate Updated!",
        description: "Your estimate has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/estimates'] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (editedEstimate) {
      const cleanData = {
        ...editedEstimate,
        items: editedEstimate.items?.map((item: any) => ({
          ...item,
          notes: item.notes || ""
        }))
      };
      
      delete cleanData.client;
      delete cleanData.project;
      delete cleanData.clientName;
      
      updateEstimateMutation.mutate(cleanData);
    }
  };

  const handleCancel = () => {
    setEditedEstimate(estimate);
    setIsEditing(false);
  };

  const updateField = (field: string, value: any) => {
    if (editedEstimate) {
      // Update field and recalculate totals if tax/discount change
      if (field === 'tax' || field === 'discount') {
        const subtotal = (editedEstimate.items || []).reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
        const taxPercent = field === 'tax' ? (parseFloat(String(value)) || 0) : (editedEstimate.tax || 0);
        const discountAmount = field === 'discount' ? (parseFloat(String(value)) || 0) : (editedEstimate.discount || 0);
        const taxAmount = subtotal * (taxPercent / 100);
        const total = subtotal + taxAmount - discountAmount;
        setEditedEstimate({ ...editedEstimate, [field]: value, subtotal, total });
        return;
      }
      setEditedEstimate({ ...editedEstimate, [field]: value });
    }
  };

  const updateItem = (index: number, field: string, value: any) => {
    if (editedEstimate && editedEstimate.items) {
      const newItems = [...editedEstimate.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'unitPrice') {
        newItems[index].amount = (newItems[index].quantity || 0) * (newItems[index].unitPrice || 0);
      }
      
      const subtotal = newItems.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
      const taxPercent = parseFloat(String(editedEstimate.tax || 0)) || 0;
      const taxAmount = subtotal * (taxPercent / 100);
      const discountAmount = parseFloat(String(editedEstimate.discount || 0)) || 0;
      const total = subtotal + taxAmount - discountAmount;
      
      setEditedEstimate({
        ...editedEstimate,
        items: newItems,
        subtotal,
        total
      });
    }
  };

  /** When user picks a service from the dropdown, apply its type, description, unit, and price in one update and recalc amount/totals */
  const applyServiceToItem = (index: number, service: { value: string; label: string; unit?: string; laborRate?: number }) => {
    if (!editedEstimate?.items?.[index]) return;
    const newItems = [...editedEstimate.items];
    const item = newItems[index];
    const quantity = item.quantity ?? 1;
    const unitPrice = service.laborRate ?? 0;
    newItems[index] = {
      ...item,
      serviceType: service.value,
      description: service.label ?? item.description,
      unit: service.unit || 'unit',
      unitPrice,
      amount: quantity * unitPrice
    };
    const subtotal = newItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
    const taxPercent = parseFloat(String(editedEstimate.tax || 0)) || 0;
    const taxAmount = subtotal * (taxPercent / 100);
    const discountAmount = parseFloat(String(editedEstimate.discount || 0)) || 0;
    const total = subtotal + taxAmount - discountAmount;
    setEditedEstimate({ ...editedEstimate, items: newItems, subtotal, total });
  };

  // AI description generation per item
  const generateDescription = async (index: number) => {
    if (!editedEstimate?.items?.[index]) return;
    const item = editedEstimate.items[index];
    const quantity = parseLineItemQuantity(item.quantity);
    if (!quantity) {
      toast({
        title: 'Quantity required',
        description: 'Enter the quantity for this line item before generating a description.',
        variant: 'destructive',
      });
      return;
    }

    setGeneratingDescription(index);
    try {
      const svc = SERVICE_TYPES.find((s) => s.value === item.serviceType);
      const response = await fetch('/api/protected/ai/generate-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceType: item.serviceType,
          serviceName: svc?.label || item.serviceType || 'Service',
          quantity,
          unit: formatEstimateUnitLabel(item.unit),
          unitPrice: item.unitPrice,
          userDescription: buildAiUserDescription(item.notes, item.description),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data?.description) {
          updateItem(index, 'description', data.description);
          if (data.generatedBy === 'fallback') {
            toast({
              title: 'Basic description used',
              description: 'AI was unavailable. A template was used — review and edit before sending.',
              variant: 'destructive',
            });
          } else {
            toast({ title: 'AI description generated successfully' });
          }
        }
      } else {
        toast({ title: 'Failed to generate description', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Failed to generate description', variant: 'destructive' });
    } finally {
      setGeneratingDescription(null);
    }
  };

  const addItem = () => {
    if (editedEstimate) {
      const newItem = {
        serviceType: 'Custom',
        description: '',
        quantity: 1,
        unit: 'unit',
        unitPrice: 0, // FIXED: Changed from unit_price to unitPrice
        amount: 0,
        notes: ''
      };
      
      const newItems = [...(editedEstimate.items || []), newItem];
      const subtotal = newItems.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
      const taxPercent = parseFloat(String(editedEstimate.tax || 0)) || 0;
      const taxAmount = subtotal * (taxPercent / 100);
      const discountAmount = parseFloat(String(editedEstimate.discount || 0)) || 0;
      const total = subtotal + taxAmount - discountAmount;
      
      setEditedEstimate({
        ...editedEstimate,
        items: newItems,
        subtotal,
        total
      });
    }
  };

  const removeItem = (index: number) => {
    if (editedEstimate && editedEstimate.items) {
      const newItems = editedEstimate.items.filter((_: any, i: number) => i !== index);
      const subtotal = newItems.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
      const taxPercent = parseFloat(String(editedEstimate.tax || 0)) || 0;
      const taxAmount = subtotal * (taxPercent / 100);
      const discountAmount = parseFloat(String(editedEstimate.discount || 0)) || 0;
      const total = subtotal + taxAmount - discountAmount;
      
      setEditedEstimate({
        ...editedEstimate,
        items: newItems,
        subtotal,
        total
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="flex items-center justify-center h-screen">
              <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading estimate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!editedEstimate) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="flex items-center justify-center h-screen">
              <div className="text-center">
            <p className="text-gray-600">Estimate not found</p>
            <Button onClick={() => setLocation("/estimates")} className="mt-4">
                  Back to Estimates
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 p-8 pt-24 space-y-8 min-h-screen bg-[var(--dash-bg)]">
            {/* Page Header with Remodra branding - matching estimates page */}
            <div className="remodra-card p-6 mb-6">
              <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    onClick={() => setLocation("/estimates")}
                    className="text-slate-700 dark:text-slate-300 hover:text-green-500 dark:hover:text-green-500 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Estimates
                  </Button>
                  <img 
                    src="/remodra-logo.png" 
                    alt="Remodra Logo" 
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <h1 className="text-2xl font-bold text-green-500">
                      Estimate #{editedEstimate?.estimate_number || editedEstimate?.estimateNumber || 'N/A'}
                    </h1>
                    <p className="text-slate-300 text-sm">
                      {editedEstimate?.client ? (
                        <>
                          {editedEstimate.client.first_name || editedEstimate.client.firstName || ''} {editedEstimate.client.last_name || editedEstimate.client.lastName || ''}
                          {editedEstimate.client.company_name && ` • ${editedEstimate.client.company_name}`}
                        </>
                      ) : (
                        editedEstimate?.clientName || 'Edit estimate details'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  {!isEditing ? (
                    <Button
                      onClick={() => setIsEditing(true)}
                      className="remodra-button"
                    >
                      <Edit3 className="h-5 w-5 mr-2" />
                      Edit Estimate
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={updateEstimateMutation.isPending}
                        className="remodra-button-outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={updateEstimateMutation.isPending}
                        className="remodra-button"
                      >
                        <Save className="h-5 w-5 mr-2" />
                        {updateEstimateMutation.isPending ? "Saving..." : "Save Changes"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Assigned Agent (project coordinator for client portal) */}
            <div className="remodra-card p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-green-500">Assigned Agent</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Project coordinator shown in client portal. Reassign if needed.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 max-w-xs">
                  <Label className="text-slate-700 dark:text-slate-300">Project coordinator</Label>
                  <Select
                    value={editedEstimate?.agentId != null ? String(editedEstimate.agentId) : (editedEstimate?.agent_id != null ? String(editedEstimate.agent_id) : 'unassigned')}
                    onValueChange={(value) => {
                      const newAgentId = value === 'unassigned' ? null : Number(value);
                      updateEstimateAgentMutation.mutate(
                        { id: estimateId!, data: { agentId: newAgentId } },
                        {
                          onSuccess: () => {
                            queryClient.invalidateQueries({ queryKey: ['/api/protected/estimates', estimateId] });
                            setEditedEstimate((prev: any) => (prev ? { ...prev, agentId: newAgentId, agent_id: newAgentId } : prev));
                            toast({ title: 'Agent updated', description: 'Client portal will show the selected agent.' });
                          },
                          onError: (err: Error) => {
                            toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
                          },
                        }
                      );
                    }}
                    disabled={updateEstimateAgentMutation.isPending}
                  >
                    <SelectTrigger className="mt-2 w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {(agentsList as { id: number; firstName?: string; lastName?: string; email?: string }[]).map((a) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {[a.firstName, a.lastName].filter(Boolean).join(' ') || `Agent #${a.id}`}
                          {a.email ? ` (${a.email})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editedEstimate && (editedEstimate.agentId ?? editedEstimate.agent_id) && (() => {
                  const aid = editedEstimate.agentId ?? editedEstimate.agent_id;
                  const current = (agentsList as { id: number; firstName?: string; lastName?: string; email?: string; phone?: string }[]).find((a) => a.id === aid);
                  if (!current) return null;
                  return (
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2 sm:mt-0">
                      <span className="font-medium text-slate-800 dark:text-slate-200">Current: </span>
                      {[current.firstName, current.lastName].filter(Boolean).join(' ')} — {current.email || 'No email'}
                      {current.phone ? ` · ${formatPhone(current.phone)}` : ''}
                    </div>
                  );
                })()}
              </div>
            </div>

          {/* Estimate Content - Properly Aligned */}
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Estimate Header */}
            <div className="remodra-card">
              <div className="remodra-card-padding border-b border-slate-600">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">ESTIMATE</h1>
                    <p className="text-slate-600 dark:text-slate-400 text-lg mt-2">
                      #{editedEstimate?.estimate_number || editedEstimate?.estimateNumber || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-600 dark:text-slate-400 text-sm mb-2">Status</div>
                    {isEditing ? (
                      <Select
                        value={editedEstimate?.status || 'draft'}
                        onValueChange={(value) => updateField('status', value)}
                      >
                        <SelectTrigger className="min-w-[140px] rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                        editedEstimate?.status === 'accepted' ? 'bg-green-500 text-white' :
                        editedEstimate?.status === 'rejected' ? 'bg-red-500 text-white' :
                        editedEstimate?.status === 'sent' ? 'bg-blue-500 text-white' :
                        'bg-slate-500 text-white'
                      }`}>
                        {(editedEstimate?.status || 'draft').toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="remodra-card-padding">
                {/* Billing Info */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-3">Bill To:</h3>
                  <div className="rounded-lg bg-[var(--dash-border)]/15 p-4">
                    {editedEstimate?.client ? (
                      <>
                        <div className="mb-4">
                          <p className="font-bold text-slate-800 dark:text-slate-200 text-lg mb-1">
                            {editedEstimate.client.first_name || editedEstimate.client.firstName || ''} {editedEstimate.client.last_name || editedEstimate.client.lastName || ''}
                          </p>
                          {editedEstimate.client.company_name && (
                            <p className="text-green-600 dark:text-green-400 font-medium text-sm mb-2">
                              {editedEstimate.client.company_name}
                            </p>
                          )}
                        </div>
                        
                        {(editedEstimate.client.address || editedEstimate.client.street_address) && (
                          <div className="mb-3">
                            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                              {editedEstimate.client.address || editedEstimate.client.street_address}
                              {editedEstimate.client.city && `, ${editedEstimate.client.city}`}
                              {editedEstimate.client.state && `, ${editedEstimate.client.state}`}
                              {editedEstimate.client.zip && ` ${editedEstimate.client.zip}`}
                            </p>
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          {editedEstimate.client.phone && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 dark:text-slate-400 text-xs">📞</span>
                              <span className="text-slate-600 dark:text-slate-400 text-sm">{formatPhone(editedEstimate.client.phone)}</span>
                            </div>
                          )}
                          {editedEstimate.client.email && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 dark:text-slate-400 text-xs">✉️</span>
                              <span className="text-slate-600 dark:text-slate-400 text-sm">{editedEstimate.client.email}</span>
                            </div>
                          )}
                        </div>
                      </>
                    ) : editedEstimate?.clientName ? (
                      <div>
                        <p className="font-bold text-slate-800 dark:text-slate-200 text-lg mb-2">{editedEstimate.clientName}</p>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">Client information loading...</p>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-slate-400 text-sm mb-2">Client information not available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates and Project Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="rounded-lg bg-[var(--dash-border)]/15 p-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 text-sm">Issue Date</h4>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editedEstimate?.issue_date ? String(editedEstimate.issue_date).slice(0,10) : ''}
                        onChange={(e) => updateField('issue_date', e.target.value)}
                        className="remodra-input"
                      />
                    ) : (
                      <p className="text-slate-600 dark:text-slate-400 text-sm">
                        {editedEstimate?.issue_date ? format(new Date(editedEstimate.issue_date), 'MMM dd, yyyy') : 'Not specified'}
                      </p>
                    )}
                  </div>
                  {editedEstimate?.expiry_date && (
                    <div className="rounded-lg bg-[var(--dash-border)]/15 p-4">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 text-sm">Expires</h4>
                      {isEditing ? (
                        <Input
                          type="date"
                          value={editedEstimate?.expiry_date ? String(editedEstimate.expiry_date).slice(0,10) : ''}
                          onChange={(e) => updateField('expiry_date', e.target.value)}
                          className="remodra-input"
                        />
                      ) : (
                        <p className="text-slate-600 dark:text-slate-400 text-sm">
                          {format(new Date(editedEstimate.expiry_date), 'MMM dd, yyyy')}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 p-4 rounded border border-green-200 dark:border-green-700">
                    <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2 text-sm">Estimate Total</h4>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(editedEstimate?.total || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>



            {/* Items Table */}
            <div className="remodra-card">
              <div className="remodra-card-padding border-b border-slate-600">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-green-500" />
                    Estimate Items
                  </h3>
                  {isEditing && (
                    <Button
                      onClick={addItem}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  )}
                </div>
              </div>

              <div className="remodra-card-padding">
              {editedEstimate.items && editedEstimate.items.length > 0 ? (
                <>
                  {/* Desktop: Table Layout */}
                  <div className="hidden lg:block overflow-hidden rounded-md">
                    <table className="w-full table-fixed">
                      <colgroup>
                        <col className="w-[12%]" />
                        <col className="w-[40%]" />
                        <col className="w-[8%]" />
                        <col className="w-[10%]" />
                        <col className="w-[12%]" />
                        <col className="w-[10%]" />
                        {isEditing && <col className="w-[8%]" />}
                      </colgroup>
                      <thead className="border-b border-[var(--dash-border)] bg-[var(--dash-border)]/30">
                        <tr>
                          <th className="text-left p-4 font-semibold text-base text-[var(--dash-text)]">Service</th>
                          <th className="text-left p-4 font-semibold text-base text-[var(--dash-text)]">Description</th>
                          <th className="text-right p-4 font-semibold text-base text-[var(--dash-text)]">Qty</th>
                          <th className="text-right p-4 font-semibold text-base text-[var(--dash-text)]">Unit</th>
                          <th className="text-right p-4 font-semibold text-base text-[var(--dash-text)]">Unit Price</th>
                          <th className="text-right p-4 font-semibold text-base text-[var(--dash-text)]">Amount</th>
                          {isEditing && <th className="text-center p-4 font-semibold text-base text-[var(--dash-text)]">Actions</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--dash-border)]">
                        {editedEstimate.items.map((item: any, index: number) => (
                          <tr key={index} className="hover:bg-[var(--dash-border)]/15 transition-colors">
                            <td className="p-4">
                              {isEditing ? (
                                <div className="space-y-1.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-medium text-[var(--dash-text-muted)]">Service</span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs shrink-0 remodra-button-outline"
                                      onClick={() => {
                                        addServiceLineRef.current = index;
                                        setAddServiceOpen(true);
                                      }}
                                    >
                                      <Wrench className="h-3.5 w-3.5 mr-1" />
                                      New service
                                    </Button>
                                  </div>
                                  <Select 
                                  value={item.serviceType || item.service_type || ''} 
                                  onValueChange={(value) => {
                                    if (value === 'other') {
                                      const customName = prompt('Enter custom service name:');
                                      if (customName) {
                                        updateItem(index, 'serviceType', customName);
                                        updateItem(index, 'description', customName);
                                      }
                                    } else {
                                      const service = Array.isArray(services) ? services.find((s: any) => s.value === value) : null;
                                      if (service) {
                                        applyServiceToItem(index, service);
                                      } else {
                                        updateItem(index, 'serviceType', value);
                                      }
                                    }
                                  }}
                                  disabled={servicesLoading}
                                >
                                  <SelectTrigger className="h-10 w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0 [&>span]:line-clamp-1">
                                    <SelectValue placeholder={servicesLoading ? "Loading services..." : "Select service"} />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[300px] z-50 bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                                    {Array.isArray(services) && services.length > 0 ? (
                                      services.map((service: any) => (
                                        <SelectItem key={service.value} value={service.value}>
                                          {service.label}
                                        </SelectItem>
                                      ))
                                    ) : (
                                      <SelectItem value="" disabled>
                                        {servicesLoading ? "Loading..." : "No services available"}
                                      </SelectItem>
                                    )}
                                    <SelectItem value="other">Other (Custom)</SelectItem>
                                  </SelectContent>
                                </Select>
                                </div>
                              ) : (
                                <span className="text-[var(--dash-text)] font-medium text-sm">
                                  {formatServiceLabel(item.serviceType || item.service_type)}
                                </span>
                              )}
                            </td>
                          <td className="p-4">
                            {isEditing ? (
                              <div className="flex gap-2 items-start">
                                <Textarea
                                  value={item.description || ''}
                                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                                  className="min-h-[200px] w-full resize-y flex-1 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-sm text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)] focus:border-[var(--dash-accent)] hover:border-[var(--dash-border)] transition-colors"
                                  placeholder="Item description..."
                                  rows={8}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => generateDescription(index)}
                                  disabled={generatingDescription === index}
                                  className="remodra-button-outline"
                                >
                                  {generatingDescription === index ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium text-[var(--dash-text)] text-sm">{item.description || 'No description'}</div>
                                {item.notes && (
                                  <div className="text-xs text-[var(--dash-text-muted)] mt-1">{item.notes}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={item.quantity || 0}
                                onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-28 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-right text-sm text-[var(--dash-text)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)]"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <span className="font-medium text-[var(--dash-text)] text-sm">{item.quantity || 0}</span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            {isEditing ? (
                              <Select
                                value={item.unit || 'unit'}
                                onValueChange={(value) => updateItem(index, 'unit', value)}
                              >
                                <SelectTrigger className="h-10 w-36 text-left rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0 [&>span]:line-clamp-1">
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[280px] bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                                  {mergeEstimateUnitOptions(item.unit).map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-[var(--dash-text)] font-medium text-sm">
                                {formatEstimateUnitLabel(item.unit)}
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={item.unitPrice || 0}
                                onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                className="w-32 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-right text-sm text-[var(--dash-text)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)]"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <span className="font-medium text-[var(--dash-text)] text-sm">{formatCurrency(item.unitPrice || 0)}</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-medium text-green-500 text-sm">
                            {formatCurrency(item.amount || 0)}
                          </td>
                          {isEditing && (
                            <td className="p-3 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeItem(index)}
                                className="remodra-button-outline"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                  {/* Mobile: Card Layout */}
                  <div className="lg:hidden space-y-4">
                    {editedEstimate.items.map((item: any, index: number) => (
                      <div key={index} className="rounded-lg bg-[var(--dash-bg)] p-4 ring-1 ring-[var(--dash-border)]/40">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1 space-y-1.5 min-w-0">
                            {isEditing ? (
                              <>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-[var(--dash-text-muted)]">Service</span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs shrink-0 remodra-button-outline"
                                    onClick={() => {
                                      addServiceLineRef.current = index;
                                      setAddServiceOpen(true);
                                    }}
                                  >
                                    <Wrench className="h-3.5 w-3.5 mr-1" />
                                    New service
                                  </Button>
                                </div>
                              <Select 
                                value={item.serviceType || item.service_type || ''} 
                                onValueChange={(value) => {
                                  if (value === 'other') {
                                    const customName = prompt('Enter custom service name:');
                                    if (customName) {
                                      updateItem(index, 'serviceType', customName);
                                      updateItem(index, 'description', customName);
                                    }
                                  } else {
                                    const service = Array.isArray(services) ? services.find((s: any) => s.value === value) : null;
                                    if (service) {
                                      applyServiceToItem(index, service);
                                    } else {
                                      updateItem(index, 'serviceType', value);
                                    }
                                  }
                                }}
                                disabled={servicesLoading}
                              >
                                <SelectTrigger className="h-10 w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0 [&>span]:line-clamp-1">
                                  <SelectValue placeholder={servicesLoading ? "Loading services..." : "Select service"} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] z-50 bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                                  {Array.isArray(services) && services.length > 0 ? (
                                    services.map((service: any) => (
                                      <SelectItem key={service.value || service.serviceType} value={service.value || service.serviceType}>
                                        {service.label || service.name}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <SelectItem value="" disabled>
                                      {servicesLoading ? "Loading..." : "No services available"}
                                    </SelectItem>
                                  )}
                                  <SelectItem value="other">Other (Custom)</SelectItem>
                                </SelectContent>
                              </Select>
                              </>
                            ) : (
                              <span className="text-[var(--dash-text)] font-medium text-sm">
                                {formatServiceLabel(item.serviceType || item.service_type)}
                              </span>
                            )}
                          </div>
                          {isEditing && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="remodra-button-outline ml-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="mb-4">
                          <Label className="text-sm font-medium text-[var(--dash-text)] mb-2 block">Description</Label>
                          {isEditing ? (
                            <div className="flex gap-2 items-start">
                              <Textarea
                                value={item.description || ''}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                className="min-h-[120px] w-full resize-y flex-1 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-sm text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)] hover:border-[var(--dash-border)] transition-colors"
                                placeholder="Item description..."
                                rows={5}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => generateDescription(index)}
                                disabled={generatingDescription === index}
                                className="remodra-button-outline"
                              >
                                {generatingDescription === index ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          ) : (
                            <p className="text-[var(--dash-text)] text-sm">{item.description || 'No description'}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Quantity</Label>
                            {isEditing ? (
                              <Input
                                type="number"
                                value={item.quantity || 0}
                                onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="remodra-input w-full"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <p className="text-slate-700 dark:text-slate-300">{item.quantity || 0}</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-[var(--dash-text)] mb-2 block">Unit</Label>
                            {isEditing ? (
                              <Select
                                value={item.unit || 'unit'}
                                onValueChange={(value) => updateItem(index, 'unit', value)}
                              >
                                <SelectTrigger className="h-10 w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-3 py-2 text-sm text-[var(--dash-text)] focus:ring-2 focus:ring-[var(--dash-accent)] focus:ring-offset-0">
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[280px] bg-[var(--dash-card)] border-[var(--dash-border)] text-[var(--dash-text)]">
                                  {mergeEstimateUnitOptions(item.unit).map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-[var(--dash-text)]">{formatEstimateUnitLabel(item.unit)}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-medium text-[var(--dash-text)] mb-2 block">Unit Price</Label>
                            {isEditing ? (
                              <Input
                                type="number"
                                value={item.unitPrice || 0}
                                onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                className="remodra-input w-full"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <p className="text-[var(--dash-text)]">{formatCurrency(item.unitPrice || 0)}</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Amount</Label>
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(item.amount || 0)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-400 bg-slate-700 rounded-lg border-2 border-dashed border-slate-600">
                  <Wrench className="h-8 w-8 mx-auto text-slate-500 mb-3" />
                  <p className="text-base font-medium text-slate-300 mb-2">No items in this estimate</p>
                  <p className="text-sm text-slate-400 mb-3">Add items to start building your estimate</p>
                  {isEditing && (
                    <Button onClick={addItem} size="sm" className="bg-green-500 hover:bg-green-600 text-slate-900 font-semibold">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Item
                    </Button>
                  )}
                </div>
              )}
              </div>
          </div>

            {/* Pricing Summary - Dark SaaS Style */}
            <div className="remodra-card">
              <div className="remodra-card-padding border-b border-slate-600">
                <h3 className="text-lg font-bold text-[var(--dash-text)] flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-green-500" />
                  Pricing Summary
                </h3>
              </div>
              
              <div className="remodra-card-padding">
                <div className="flex justify-end">
                  <div className="w-80 max-w-full space-y-3 rounded-lg bg-[var(--dash-border)]/20 p-4">
                      <div className="flex justify-between items-center py-2 border-b border-slate-600">
                        <span className="text-slate-300 font-medium text-sm">Subtotal:</span>
                        <span className="font-semibold text-white text-sm">{formatCurrency(editedEstimate?.subtotal || 0)}</span>
                      </div>
                      {isEditing ? (
                        <>
                          <div className="space-y-2 py-2">
                            <div className="flex justify-between items-center">
                              <Label htmlFor="tax" className="text-sm text-slate-300 font-medium">Tax (%):</Label>
                              <Input
                                id="tax"
                                type="number"
                                value={editedEstimate?.tax || 0}
                                onChange={(e) => updateField('tax', parseFloat(e.target.value) || 0)}
                                className="remodra-input w-24 text-right"
                                min="0"
                                max="100"
                                step="0.01"
                              />
                            </div>
                            {(() => {
                              const subtotal = editedEstimate?.subtotal || 0;
                              const taxPercent = parseFloat(String(editedEstimate?.tax || 0)) || 0;
                              const taxAmount = subtotal * (taxPercent / 100);
                              return taxPercent > 0 ? (
                                <div className="text-xs text-slate-400 text-right">
                                  = {formatCurrency(taxAmount)}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <Label htmlFor="discount" className="text-sm text-slate-300 font-medium">Discount ($):</Label>
                            <Input
                              id="discount"
                              type="number"
                              value={editedEstimate?.discount || 0}
                              onChange={(e) => updateField('discount', parseFloat(e.target.value) || 0)}
                              className="remodra-input w-24 text-right"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          {(() => {
                            const subtotal = editedEstimate?.subtotal || 0;
                            const taxPercent = parseFloat(String(editedEstimate?.tax || 0)) || 0;
                            const taxAmount = subtotal * (taxPercent / 100);
                            const discountAmount = parseFloat(String(editedEstimate?.discount || 0)) || 0;
                            
                            return (
                              <>
                                {taxPercent > 0 && (
                                  <div className="flex justify-between items-center py-2 border-b border-slate-600">
                                    <span className="text-slate-300 text-sm">Tax ({taxPercent}%):</span>
                                    <span className="font-medium text-white text-sm">{formatCurrency(taxAmount)}</span>
                                  </div>
                                )}
                                {discountAmount > 0 && (
                                  <div className="flex justify-between items-center py-2 border-b border-slate-600">
                                    <span className="text-slate-300 text-sm">Discount:</span>
                                    <span className="font-medium text-rose-400 text-sm">-{formatCurrency(discountAmount)}</span>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                      <Separator className="my-3" />
                      <div className="flex justify-between items-center py-3 px-4 rounded-lg border border-green-700 bg-green-900/40">
                        <span className="text-lg font-bold text-green-100">Total:</span>
                        <span className="text-xl font-bold text-green-100">{formatCurrency(editedEstimate?.total || 0)}</span>
                      </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms and Notes - Dark SaaS Style */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Terms and Conditions */}
              <div className="remodra-card">
                <div className="remodra-card-padding border-b border-slate-600">
                  <h3 className="text-lg font-bold text-[var(--dash-text)] flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-green-500" />
                    Terms & Conditions
                  </h3>
                </div>

                <div className="remodra-card-padding">
                  {isEditing ? (
                    <Textarea
                      value={editedEstimate?.terms || "Payment due within 30 days. Work to begin upon acceptance of estimate. All work performed to industry standards with warranty on materials and workmanship."}
                      onChange={(e) => updateField('terms', e.target.value)}
                      placeholder="Payment terms, project conditions, warranties, and client responsibilities..."
                      className="min-h-[100px] w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-sm text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)]"
                    />
                  ) : (
                    <div className="prose max-w-none">
                      <p className="text-[var(--dash-text)] leading-relaxed text-sm">
                        {editedEstimate?.terms || "Payment due within 30 days. Work to begin upon acceptance of estimate. All work performed to industry standards with warranty on materials and workmanship."}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="remodra-card">
                <div className="remodra-card-padding border-b border-slate-600">
                  <h3 className="text-lg font-bold text-[var(--dash-text)] flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-green-500" />
                    Additional Notes
                  </h3>
                </div>
                
                <div className="remodra-card-padding">
                  {isEditing ? (
                    <Textarea
                      value={editedEstimate?.notes || ""}
                      onChange={(e) => updateField('notes', e.target.value)}
                      placeholder="Add any additional notes, special instructions, or project requirements..."
                      className="min-h-[100px] w-full rounded-lg border border-[var(--dash-border)] bg-[var(--dash-bg)] px-4 py-3 text-sm text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--dash-accent)]"
                    />
                  ) : (
                    <div className="prose max-w-none">
                      <p className="text-[var(--dash-text)] leading-relaxed text-sm">
                        {editedEstimate?.notes || "No additional notes provided."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </main>
        </div>
      </div>
      <AddServiceDialog
        open={addServiceOpen}
        onOpenChange={(open) => {
          setAddServiceOpen(open);
          if (!open) addServiceLineRef.current = null;
        }}
        onCreated={({ serviceType, laborRate, displayName }) => {
          const idx = addServiceLineRef.current;
          if (idx === null) return;
          applyServiceToItem(idx, {
            value: serviceType,
            label: displayName ?? serviceType,
            laborRate,
          });
          addServiceLineRef.current = null;
        }}
      />
    </div>
  );
}

